import {
  selectIsPanoAligned,
  selectIsPanoWithDepth,
} from "@/modes/walk-mode/walk-scene-selectors";
import {
  FeaturesState,
  OnlyFeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { selectProjectFilteringSlice } from "@/store/project-filtering/project-filtering-selectors";
import {
  OnlyProjectFilteringStore,
  ProjectFilteringSlice,
} from "@/store/project-filtering/project-filtering-slice";
import { curryAppSelector } from "@/store/reselect";
import { selectPanoAnnotationContainer } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectFilteredElementsWithTags } from "@/store/tags/tags-selectors";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { selectPanoAnnotationsAdjustedPose } from "@/utils/camera-transform";
import { assert } from "@faro-lotv/foundation";
import {
  IElementGenericAnnotation,
  IElementImg360,
  IElementTypeHint,
  isIElementDataSetVideoWalk,
  isIElementGenericAnnotation,
  isIElementImg2d,
  isIElementImg360,
  isIElementMarkup,
  isIElementPanoInOdometryPath,
  isIElementSectionFlash,
  isIElementSectionWithTypeHint,
  isSpaceAnnotation,
  type IElement,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectHasValidPose,
  selectIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useMemo } from "react";
import { Matrix4 } from "three";
import { FlyWalkSceneActiveElement } from "../flywalk-types";

export type AnnotationDispatch = {
  /** Spatial annotations that can be collapsed if they are far away */
  annotations: IElementGenericAnnotation[];
  /** Annotations that should not be collapsed */
  notCollapsibleAnnotations: IElementGenericAnnotation[];
  /** Whether the main element is a panorama */
  isPano: boolean;
  /** Whether the main element is a pano with valid pose */
  isPanoWithValidPose: boolean;
  /** Whether the panorama is aligned */
  isPanoAligned: boolean;
  /** Whether the panorama has depth information */
  isPanoWithDepth: boolean;
  /** Whether the pano is paired with a point cloud for picking */
  isPanoPairedWithPickingPointCloud: boolean;
  /** Optional transform to render pano annotations */
  panoAdjustedPose: Matrix4 | undefined;
};

/**
 *
 * @param annotations All annotations of the current scene
 * @param mainElement The current main element
 * @returns The relevant annotations for the current element, listed in spatial and not collapsible.
 */
export function useDispatchAnnotations(
  annotations: IElementGenericAnnotation[],
  mainElement: FlyWalkSceneActiveElement | null,
): AnnotationDispatch {
  const spaceAnnotations = useMemo(
    () => annotations.filter(isSpaceAnnotation),
    [annotations],
  );
  return useAppSelector(
    selectAnnotationDispatchData(mainElement, spaceAnnotations),
    isEqual,
  );
}

type ElementsFeaturesFilteringStore = OnlyIElementsStore &
  OnlyFeaturesState &
  OnlyProjectFilteringStore;

const selectAnnotationDispatchData = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFeaturesSlice,
      selectProjectFilteringSlice,
      (
        state: ElementsFeaturesFilteringStore,
        iElement: FlyWalkSceneActiveElement | null,
      ) => iElement,
      (
        state: ElementsFeaturesFilteringStore,
        iElement: FlyWalkSceneActiveElement | null,
        annotations: IElementGenericAnnotation[],
      ) => annotations,
    ],
    computeAnnotationDispatchData,
  ),
);

function computeAnnotationDispatchData(
  iElements: IElementsState,
  features: FeaturesState,
  projectFiltering: ProjectFilteringSlice,
  mainElement: FlyWalkSceneActiveElement | null,
  annotations: IElementGenericAnnotation[],
): AnnotationDispatch {
  const ret = {
    annotations: new Array<IElementGenericAnnotation>(),
    notCollapsibleAnnotations: new Array<IElementGenericAnnotation>(),
    isPano: !!mainElement && isIElementImg360(mainElement),
    isPanoAligned: false,
    isPanoWithDepth: false,
    isPanoWithValidPose: false,
    isPanoPairedWithPickingPointCloud: false,
    panoAdjustedPose: undefined,
  } satisfies AnnotationDispatch;

  if (
    !mainElement ||
    isIElementOrthomosaic(mainElement) ||
    isIElementImg2d(mainElement)
  ) {
    return ret;
  }

  const spaceAnnotations = annotations.filter(isSpaceAnnotation);

  if (ret.isPano) {
    assert(
      isIElementImg360(mainElement),
      "mainElement must be IElementImg360 if isPano is true",
    );
    return selectPanoAnnotationDispatchData(
      mainElement,
      spaceAnnotations,
    )({ iElements, features, projectFiltering });
  }
  ret.annotations = spaceAnnotations.filter(
    (annotation) => !annotation.metaDataMap?.isEstimatedFrom2DPano,
  );
  return ret;
}

const selectPanoAnnotationDispatchData = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFeaturesSlice,
      selectProjectFilteringSlice,
      (state: ElementsFeaturesFilteringStore, pano: IElementImg360) => pano,
      (
        state: ElementsFeaturesFilteringStore,
        pano: IElementImg360,
        annotations: IElementGenericAnnotation[],
      ) => annotations,
    ],
    computePanoAnnotationDispatchData,
  ),
);

function computePanoAnnotationDispatchData(
  iElements: IElementsState,
  features: FeaturesState,
  projectFiltering: ProjectFilteringSlice,
  pano: IElementImg360,
  annotations: IElementGenericAnnotation[],
): AnnotationDispatch {
  const isPanoAligned = selectIsPanoAligned(pano)({ iElements, features });

  const isPanoWithValidPose = selectHasValidPose(pano)({ iElements });

  const isPanoWithDepth = selectIsPanoWithDepth(pano)({ iElements });

  // Compute annotations section and adjusted pose
  const matrix = selectPanoAnnotationsAdjustedPose(pano)({ iElements });
  const panoAdjustedPose = new Matrix4().fromArray(matrix);

  const panoSection = selectPanoAnnotationContainer(pano)({ iElements });

  const generalAnnotations = selectChildrenDepthFirst(
    panoSection,
    isIElementGenericAnnotation,
  )({ iElements });

  const filteredGeneralAnnotations = selectFilteredElementsWithTags(
    generalAnnotations,
    (el) => selectChildDepthFirst(el, isIElementMarkup),
  )({ iElements, projectFiltering });

  const areaAnnotations = annotations.filter(
    (annotation) =>
      !filteredGeneralAnnotations.some((a) => a.id === annotation.id),
  );

  const isPanoPairedWithPickingPointCloud =
    selectIsPanoPairedWithPickingPointCloud(pano)({
      iElements,
    });

  return {
    annotations: areaAnnotations,
    notCollapsibleAnnotations: filteredGeneralAnnotations,
    isPano: true,
    isPanoWithValidPose,
    isPanoAligned,
    isPanoWithDepth,
    isPanoPairedWithPickingPointCloud,
    panoAdjustedPose,
  };
}

/**
 * This selector checks whether a pano is paired with a picking point cloud.
 * This is the case when the pano belongs to an Orbis odometry path and there is an
 * aligned Orbis point cloud. This is also the case when the pano is a child of a Section - StructuredE57
 * since we know that such panos are always aligned.
 * If a pano is paired with a picking point cloud, and does not have depth data, then the user
 * can still take 3D measurements and 3D annotations on it via the zoom lens.
 *
 * @returns true if the given pano is paired with a picking point cloud, false otherwise
 * @param pano The pano to check
 * @param state The state of the app store
 */
export const selectIsPanoPairedWithPickingPointCloud = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, pano: IElement | undefined) => pano,
    ],
    computeIsPanoPairedWithPickingPointCloud,
  ),
);

function computeIsPanoPairedWithPickingPointCloud(
  iElements: IElementsState,
  pano: IElement | undefined,
): boolean {
  // check whether we have a pano at all
  if (!pano || !isIElementImg360(pano)) {
    return false;
  }

  // Check whether this is a pano from an Orbis odometry path.
  // Panos from video mode paths are usually not aligned.
  if (isIElementPanoInOdometryPath(pano)) {
    return !selectAncestor(pano, isIElementDataSetVideoWalk)({ iElements });
  }

  // Check whether the pano is a child of a section - flash scan
  const parent = selectIElement(pano.parentId)({ iElements });
  if (!!parent && isIElementSectionFlash(parent)) {
    return true;
  }

  // Return true if the pano is a child of a section with type hint StructuredE57
  if (
    !!parent &&
    isIElementSectionWithTypeHint(parent, IElementTypeHint.structuredE57)
  ) {
    return true;
  }
  return false;
}
